import type { Payment } from 'payments-lib-types';
import { snakify } from '@/helpers/common';
import type { CurrencyCode } from '@/helpers/currency';
import { request } from './base';

// SUBSCRIPTIONS

export interface Subscriptions {
  receivePromos: boolean;
  receiveSmsPromos: boolean;
}

export type SubscriptionParams = Partial<Subscriptions>;

export const updateProfileSubscriptions = (
  subscriptionParams: SubscriptionParams
) =>
  request<void>({
    method: 'PUT',
    url: 'subscriptions',
    data: snakify({ subscriptionParams }),
  });

export const removeDocument = (id: number) =>
  request<void>({
    method: 'DELETE',
    url: `player/documents/${id}`,
  });

export const createDocument = (documents: FormData) =>
  request<Document>({
    method: 'POST',
    url: 'player/documents',
    data: documents,
  });

// SESSIONS

export const removeSession = (id: number) =>
  request<void>({
    method: 'DELETE',
    url: `player/sessions/${id}`,
  });

// USER

export interface UpdatePasswordBody {
  currentPassword: string;
  email: string;
  password: string;
  passwordConfirmation: string;
}

export const updatePassword = (passwords: UpdatePasswordBody) =>
  request<{ errors: unknown }>({
    method: 'PUT',
    url: 'users',
    data: { user: snakify({ ...passwords }) },
  });

export const emailConfirmation = (email: string) =>
  request<void>({
    method: 'POST',
    url: 'users/confirmation',
    data: { user: { email } },
  });

// player/two_factor

export interface TwoFactor {
  data: string;
  otpSecret: string;
}

export const twoFactor = () =>
  request<TwoFactor>({ url: 'v2/player/two_factor' });

interface TwoFactorBody {
  authenticationCode: string;
  otpSecret: string;
}

export interface BackupCodes {
  backupCodes: string[];
}

export const activateTwoFactor = (codes: TwoFactorBody) =>
  request<BackupCodes>({
    method: 'POST',
    url: 'v2/player/two_factor',
    data: snakify({ two_factor: { ...codes } }),
  });

export const deactivateTwoFactor = (authenticationCode: string) =>
  request<BackupCodes>({
    method: 'DELETE',
    url: 'v2/player/two_factor',
    data: { two_factor: snakify({ authenticationCode }) },
  });

// GAMES

export interface HistoryGame {
  currency: CurrencyCode;
  totalBets: number;
  totalWins: number;
  game: string;
  title: string;
  provider: string;
  createdAt: string;
  finishedAt: string;
}

export const historyGames = () =>
  request<HistoryGame[]>({ url: 'player/games' });

// PROMO EVENTS
export interface PromoEvent {
  amountCents: string;
  createdAt: string;
  currency: string;
  eventType: string;
  id: number;
  prize: string;
  provider: string;
  wagerCents: string;
}

// USER LIMITS

export const createUserLimit = (userLimit: any) =>
  request({ method: 'POST', url: 'user_limits', data: snakify({ userLimit }) });

export const deleteUserLimit = (id: number) =>
  request({ method: 'DELETE', url: `user_limits/${id}` });

export const userLimitsConfirm = (token: string) =>
  request<void>({
    method: 'POST',
    url: 'user_limits/confirm',
    data: { token },
  });

// PAYMENTS

export enum ETransactionState {
  Accepted = 'accepted',
  Succeeded = 'succeeded',
  Rejected = 'rejected',
  Recalled = 'recalled',
  Reserved = 'reserved',
  Canceled = 'canceled',
  Pending = 'pending',
  Processing = 'processing',
  Declined = 'declined',
  Failed = 'failed',
}

export enum EPaymentStatusFilter {
  Pending = 'pending',
  Success = 'success',
  Failure = 'failure',
}

export interface ITransaction {
  id: number;
  amountCents: number;
  currency: CurrencyCode;
  action: Payment.Action;
  paymentSystem: string;
  recallable: boolean;
  createdAt: string;
  finishedAt: string;
  systemName: string;
  state: ETransactionState;
  brand?: string;
  success: boolean;
  cancellationReason: string;
}

export interface IPaymentsFilter {
  currency?: string;
  type?: Payment.Action;
  from?: string;
  to?: string;
  statuses?: EPaymentStatusFilter[];
}

export interface IPaymentsPageBody {
  page?: number;
  pageSize?: number;
  filter?: IPaymentsFilter;
}

export interface IPaymentsPage {
  data: ITransaction[];
  pagination: {
    page: number;
    perPage: number;
    totalCount: number;
  };
}

const compactFilter = (filter?: IPaymentsFilter) => {
  if (!filter) return;

  const compacted = Object.entries(filter).reduce<Record<string, unknown>>(
    (accumulator, [key, value]) => {
      if (value !== undefined) accumulator[key] = value;
      return accumulator;
    },
    {}
  );

  return Object.keys(compacted).length ? compacted : undefined;
};

export const playerPaymentsWithPages = async (
  { filter, ...rest }: IPaymentsPageBody,
  signal?: AbortSignal
) => {
  const { data } = await request<IPaymentsPage>({
    method: 'POST',
    url: 'player/payments/with_pages',
    data: snakify({ ...rest, filter: compactFilter(filter) }),
    signal,
    functionScopedConfig: { headers: { 'X-Content-Policy': '4' } },
  });

  return data?.pagination && Array.isArray(data.data) ? data : undefined;
};

export const recallWithdraw = (id: number) =>
  request<void>({ method: 'POST', url: `player/payments/${id}/recall` });

// REFERRAL SYSTEM

export interface ReferralCode {
  refcode: string;
  claimedAmount: number;
  completedUsersCount: number;
  toClaimAmount: number;
  usersCount: number;
}

interface AggregatedData {
  totalInvited: number;
  totalClaimed: number;
}

export interface ReferralSystem {
  referralCodes: ReferralCode[];
  aggregatedData: AggregatedData;
  currency: string;
}

export const playerReferralSystem = (currency: string) =>
  request<ReferralSystem>({
    url: `player/referral_system/list?currency=${currency}`,
  });

export const createReferralCode = (refCode: string) =>
  request<BackupCodes>({
    method: 'POST',
    url: 'player/referral_system',
    data: snakify({ refCode }),
  });

export const claimReferralCode = (refCode: string) =>
  request<BackupCodes>({
    method: 'POST',
    url: 'player/referral_system/claim',
    data: snakify({ refCode }),
  });

// GDPR

export const createGdprReport = () =>
  request<void>({
    method: 'POST',
    url: 'gdpr_reports',
    // A 5xx retry after the report was created hits the once-per-30-days limit and shows the cooldown text instead of success.
    retries: 0,
  });
